import { useState } from 'react';
import {
  Box, Flex, Text, Spinner, Button, Badge, Input, IconButton, useToast,
  Radio, RadioGroup, Stack, Divider, SimpleGrid,
} from '@chakra-ui/react';
import { MdEdit, MdDelete, MdAdd } from 'react-icons/md';
import Card from 'components/card/Card';
import Pagination, { useServerPaged, useDebounced } from 'components/vdh/Pagination';
import { api } from 'lib/api';
import { useApi } from 'lib/useApi';
import { useAuth } from 'lib/auth';

// [emoji, nhãn, gradient light, gradient dark] — dark dịu lại để không chói trên nền tối.
const SUBJ_META = {
  english: ['🇬🇧', 'Tiếng Anh', 'linear-gradient(150deg,#d9f0ff,#a7d8ff)', 'linear-gradient(150deg,#16324a,#1f4d70)'],
  japanese: ['🇯🇵', 'Tiếng Nhật', 'linear-gradient(150deg,#ffe0e0,#ffb3b3)', 'linear-gradient(150deg,#4a1f22,#6e2e34)'],
  math: ['🔢', 'Toán', 'linear-gradient(150deg,#e3e0ff,#c3b6ff)', 'linear-gradient(150deg,#2a2358,#3a2f74)'],
};
const subjMeta = (s) => SUBJ_META[s] || ['📚', s, 'linear-gradient(150deg,#eee,#ddd)', 'linear-gradient(150deg,#2a2350,#1e1840)'];

export default function Questions() {
  const { account } = useAuth();
  const isAdmin = account?.role === 'admin';
  const [subject, setSubject] = useState(null);
  const [bank, setBank] = useState(null);
  const { data, loading, error, reload } = useApi(() => api.get('/admin/banks'));

  if (!isAdmin) return <Box pt={{ base: '130px', md: '80px' }}><Card><Text>Chỉ quản trị viên mới quản lý câu hỏi.</Text></Card></Box>;
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {loading ? <Spinner /> : error ? <Text color="red.500">{error}</Text>
        : bank ? <Editor bank={bank} onBack={() => setBank(null)} />
        : subject ? <Grades subject={subject} banks={data} onBack={() => setSubject(null)} onOpen={setBank} onReload={reload} />
        : <Subjects banks={data} onOpen={setSubject} />}
    </Box>
  );
}

function Subjects({ banks, onOpen }) {
  const subjects = {};
  banks.forEach((b) => {
    subjects[b.subject] = subjects[b.subject] || { grades: 0, count: 0 };
    subjects[b.subject].grades += 1;
    subjects[b.subject].count += b._count?.questions || 0;
  });
  return (
    <>
      <Text fontWeight="850" fontSize="18px" mb="14px">📚 Ngân hàng câu hỏi — chọn môn</Text>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap="16px">
        {Object.entries(subjects).map(([s, v]) => {
          const [emo, label, grad, gradDark] = subjMeta(s);
          return (
            <Card key={s} p="0" overflow="hidden" cursor="pointer" onClick={() => onOpen(s)}
              _hover={{ transform: 'translateY(-3px)', boxShadow: '0 24px 60px rgba(85,60,160,.18)' }} transition=".18s">
              <Flex h="80px" align="center" justify="center" fontSize="40px" bg={grad} _dark={{ bg: gradDark }}>{emo}</Flex>
              <Box p="16px"><Text fontWeight="850" fontSize="16px">{label}</Text>
                <Text fontSize="13px" color="secondaryGray.600" mt="2px">{v.grades} lớp · {v.count.toLocaleString('vi-VN')} câu</Text></Box>
            </Card>
          );
        })}
      </SimpleGrid>
    </>
  );
}

function Grades({ subject, banks, onBack, onOpen, onReload }) {
  const toast = useToast();
  const [busy, setBusy] = useState('');
  const [emo, label] = subjMeta(subject);
  const list = banks.filter((b) => b.subject === subject).sort((a, b) => String(a.grade).localeCompare(String(b.grade)));
  async function publish(e, b) {
    e.stopPropagation();
    setBusy(b.grade);
    try { await api.post(`/admin/banks/${b.subject}/${b.grade}/publish`, {}); toast({ title: 'Đã publish', status: 'success' }); onReload && onReload(); }
    catch (err) { toast({ title: err.message, status: 'error' }); } finally { setBusy(''); }
  }
  return (
    <>
      <Flex align="center" gap="10px" mb="6px"><Button variant="ghost" size="sm" onClick={onBack}>‹ Môn</Button></Flex>
      <Text fontSize="12.5px" color="secondaryGray.600" mb="12px">Câu hỏi · <b>{label}</b></Text>
      <Card>
        <Text fontWeight="850" fontSize="16px" mb="12px">{emo} {label} — chọn lớp/cấp ({list.length})</Text>
        {list.map((b) => (
          <Flex key={b.id} align="center" py="11px" borderBottom="1px solid" borderColor="line" cursor="pointer" onClick={() => onOpen(b)}>
            <Box flex="1"><Text fontWeight="700">{String(b.grade).toUpperCase()}</Text>
              <Text fontSize="sm" color="secondaryGray.600">v{b.version} · {b._count?.questions ?? '—'} câu</Text></Box>
            <Badge colorScheme={b.published ? 'green' : 'gray'} me="10px">{b.published ? 'Đã publish' : 'Nháp'}</Badge>
            <Button size="sm" colorScheme="brand" isLoading={busy === b.grade} onClick={(e) => publish(e, b)}>Publish</Button>
          </Flex>
        ))}
      </Card>
    </>
  );
}

function Editor({ bank, onBack }) {
  const toast = useToast();
  const base = `/admin/banks/${bank.subject}/${bank.grade}`;
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [adding, setAdding] = useState(false);
  // Một bộ có thể vài nghìn câu -> tìm + phân trang trong DB.
  const pager = useServerPaged((p) => api.get(`${base}/questions?page=${p.page}&pageSize=${p.pageSize}${dq.trim() ? `&q=${encodeURIComponent(dq.trim())}` : ''}`),
    { initialSize: 20, resetKey: dq.trim(), deps: [bank.id] });
  const { loading, error, reload } = pager;

  if (error) return <Text color="red.500">{error}</Text>;

  return (
    <>
      <Flex align="center" mb="12px" gap="10px" wrap="wrap">
        <Button variant="ghost" onClick={onBack}>‹ Ngân hàng</Button>
        <Text fontWeight="800">{bank.subject} · {String(bank.grade).toUpperCase()}</Text>
        <Badge>{pager.total} câu</Badge>
        <Box flex="1" />
        <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm" onClick={() => setAdding((v) => !v)}>Thêm câu</Button>
        <Button size="sm" variant="outline" onClick={async () => {
          try { await api.post(`${base}/publish`, {}); toast({ title: 'Đã publish bản mới', status: 'success' }); }
          catch (e) { toast({ title: e.message, status: 'error' }); }
        }}>Publish</Button>
      </Flex>

      {adding && <QForm base={base} onDone={() => { setAdding(false); reload(); }} onCancel={() => setAdding(false)} />}

      <Card>
        <Input placeholder="🔍 Tìm câu hỏi..." value={q} onChange={(e) => setQ(e.target.value)} mb="14px" maxW="400px" />
        {pager.pageItems.map((item) => <QRow key={item.id} item={item} onChanged={reload} />)}
        {!pager.pageItems.length && <Text color="secondaryGray.600">{loading ? 'Đang tải…' : 'Không có câu nào khớp.'}</Text>}
        <Pagination pager={pager} label="câu" />
      </Card>
    </>
  );
}

function QRow({ item, onChanged }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  async function del() {
    if (!window.confirm('Xoá câu hỏi này?')) return;
    try { await api.del(`/admin/questions/${item.id}`); toast({ title: 'Đã xoá', status: 'success' }); onChanged(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  if (editing) return <Box py="12px" borderBottom="1px solid" borderColor="line"><QForm item={item} onDone={() => { setEditing(false); onChanged(); }} onCancel={() => setEditing(false)} /></Box>;
  return (
    <Flex py="12px" borderBottom="1px solid" borderColor="line" align="start">
      <Box flex="1">
        <Text fontWeight="700" mb="4px">{item.text} {item.level ? <Badge ml="6px">Lv{item.level}</Badge> : null}</Text>
        <Flex gap="8px" wrap="wrap">
          {item.options.map((o, i) => (
            <Badge key={i} colorScheme={i === item.answer ? 'green' : 'gray'} variant={i === item.answer ? 'solid' : 'subtle'}>{o}</Badge>
          ))}
        </Flex>
      </Box>
      <IconButton aria-label="Sửa" icon={<MdEdit />} size="sm" variant="ghost" onClick={() => setEditing(true)} me="4px" />
      <IconButton aria-label="Xoá" icon={<MdDelete />} size="sm" variant="ghost" colorScheme="red" onClick={del} />
    </Flex>
  );
}

function QForm({ base, item, onDone, onCancel }) {
  const toast = useToast();
  const [text, setText] = useState(item?.text || '');
  const [options, setOptions] = useState(item?.options?.length ? [...item.options] : ['', '', '', '']);
  const [answer, setAnswer] = useState(String(item?.answer ?? 0));
  const [level, setLevel] = useState(item?.level || 1);
  const [busy, setBusy] = useState(false);

  const setOpt = (i) => (e) => { const n = [...options]; n[i] = e.target.value; setOptions(n); };

  async function save() {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!text.trim() || opts.length < 2) return toast({ title: 'Cần nội dung + ít nhất 2 lựa chọn', status: 'warning' });
    const ans = Number(answer);
    if (ans < 0 || ans >= opts.length) return toast({ title: 'Đáp án đúng không hợp lệ', status: 'warning' });
    setBusy(true);
    const body = { text: text.trim(), options: opts, answer: ans, level: Number(level) };
    try {
      if (item) await api.patch(`/admin/questions/${item.id}`, body);
      else await api.post(`${base}/questions`, body);
      toast({ title: item ? 'Đã lưu' : 'Đã thêm câu', status: 'success' });
      onDone();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }

  return (
    <Card mb={item ? '0' : '16px'} bg="softBg">
      <Text fontWeight="700" mb="10px">{item ? 'Sửa câu hỏi' : 'Câu hỏi mới'}</Text>
      <Input placeholder="Nội dung câu hỏi" value={text} onChange={(e) => setText(e.target.value)} mb="12px" />
      <RadioGroup value={answer} onChange={setAnswer} mb="12px">
        <Stack>
          {options.map((o, i) => (
            <Flex key={i} align="center" gap="10px">
              <Radio value={String(i)} colorScheme="green" />
              <Input placeholder={`Lựa chọn ${i + 1}${i === 0 ? ' (nút xanh = đáp án đúng)' : ''}`} value={o} onChange={setOpt(i)} />
            </Flex>
          ))}
        </Stack>
      </RadioGroup>
      <Divider my="10px" />
      <Flex align="center" gap="12px">
        <Text fontSize="sm">Cấp độ</Text>
        <Input type="number" w="90px" value={level} onChange={(e) => setLevel(e.target.value)} min={1} max={5} />
        <Box flex="1" />
        {onCancel && <Button variant="ghost" size="sm" onClick={onCancel}>Huỷ</Button>}
        <Button colorScheme="brand" size="sm" onClick={save} isLoading={busy}>{item ? 'Lưu' : 'Thêm'}</Button>
      </Flex>
    </Card>
  );
}
